import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  MdChevronRight,
  MdDirectionsCar,
  MdRefresh,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import CoachingReportView from "./CoachingReportView";
import {
  getCurrentCoachingReport,
  getLatestCommittedAnalysisRun,
  listThreads,
} from "../services/embed";
import { useAuth } from "../hooks/useAuth";
import { useUser } from "../contexts/UserContext";

const SPIKE_FONT_FAMILY =
  "rb-freigeist-neue, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const CAR_ROW_TREATMENT = {
  icon: MdDirectionsCar,
  bg: "#e8f0fe",
  color: "#2563eb",
};

const READY_STATUSES = new Set([
  "ready",
  "complete",
  "completed",
  "committed",
  "analyzed",
  "analysis_complete",
  "analysis_completed",
]);

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function sameWeek(a, b) {
  return startOfWeek(a).getTime() === startOfWeek(b).getTime();
}

function formatMonthDay(date) {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatActivityDate(date) {
  if (!date) return "No activity yet";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function getActivityDate(thread) {
  return (
    parseDate(thread?.last_recorded_at) ||
    parseDate(thread?.last_analyzed_at) ||
    parseDate(thread?.updated_at) ||
    parseDate(thread?.created_at)
  );
}

function getThreadStatus(thread) {
  const status = String(
    thread?.status || thread?.analysis_status || thread?.state || "",
  ).toLowerCase();
  const ready = Boolean(thread?.last_analyzed_at) || READY_STATUSES.has(status);
  return ready
    ? { label: "Ready", colorScheme: "green", ready: true }
    : { label: "Processing", colorScheme: "orange", ready: false };
}

function getDisplayTitle(thread) {
  const title = String(thread?.title || "").trim();
  if (title) return title;
  return "Untitled";
}

function getTreatment() {
  return CAR_ROW_TREATMENT;
}

function pluralizeRecordings(count) {
  const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
  return `${safeCount} recording${safeCount === 1 ? "" : "s"}`;
}

function displayNameFromUserName(userName) {
  if (!userName) return "there";
  const localPart = String(userName).split("@")[0] || "there";
  const first = localPart.split(/[._\s-]+/).filter(Boolean)[0] || localPart;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

function firstText(...values) {
  return values.find((value) => typeof value === "string" && value.trim())
    ?.trim();
}

function fullNameFromParts(...parts) {
  const name = parts.filter((part) => typeof part === "string" && part.trim())
    .join(" ")
    .trim();
  return name || null;
}

function resolveIdentity({ authUserName, userInfo, tokenParsed }) {
  const fullName = firstText(
    userInfo?.name,
    fullNameFromParts(userInfo?.first_name, userInfo?.last_name),
    fullNameFromParts(userInfo?.firstName, userInfo?.lastName),
    tokenParsed?.name,
    fullNameFromParts(tokenParsed?.given_name, tokenParsed?.family_name),
  );
  const accountName = firstText(
    fullName,
    userInfo?.email,
    userInfo?.username,
    userInfo?.preferred_username,
    authUserName,
    tokenParsed?.email,
    tokenParsed?.preferred_username,
    tokenParsed?.preferredUsername,
    tokenParsed?.sub,
  );
  const greetingSeed = firstText(
    userInfo?.first_name,
    userInfo?.firstName,
    tokenParsed?.given_name,
    fullName,
    accountName,
  );

  return {
    accountName: accountName || "Unknown",
    greetingName: displayNameFromUserName(greetingSeed),
  };
}

function greetingForDate(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function groupThreadsByWeek(threads) {
  const now = new Date();
  const thisWeekStart = startOfWeek(now);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  const buckets = new Map();
  for (const thread of threads) {
    const activityDate = thread.activityDate;
    const key = activityDate
      ? startOfWeek(activityDate).toISOString()
      : "undated";
    if (!buckets.has(key)) {
      let label = "Undated";
      if (activityDate) {
        const weekStart = startOfWeek(activityDate);
        if (sameWeek(weekStart, thisWeekStart)) {
          label = "Recent Threads";
        } else if (sameWeek(weekStart, lastWeekStart)) {
          label = "Last week";
        } else {
          label = `Week of ${formatMonthDay(weekStart)}`;
        }
      }
      buckets.set(key, {
        key,
        label,
        sortTime: activityDate ? startOfWeek(activityDate).getTime() : -1,
        items: [],
      });
    }
    buckets.get(key).items.push(thread);
  }

  return Array.from(buckets.values()).sort((a, b) => b.sortTime - a.sortTime);
}

function extractLatestRun(response) {
  const items =
    response?.items ||
    response?.runs ||
    (Array.isArray(response) ? response : null) ||
    (response ? [response] : []);
  return items[0] || null;
}

function unwrapAnalysisJson(run) {
  if (!run) return null;
  return run.analysis_json ?? run.analysis ?? run.result ?? run;
}

function isAnalysisReady(analysisJson) {
  if (!analysisJson || typeof analysisJson !== "object") return false;
  if (
    Array.isArray(analysisJson?.coaching_observations) &&
    analysisJson.coaching_observations.length > 0
  ) {
    return true;
  }
  if (
    typeof analysisJson?.summary === "string" &&
    analysisJson.summary.trim().length > 0
  ) {
    return true;
  }
  if (
    typeof analysisJson?.interaction_summary === "string" &&
    analysisJson.interaction_summary.trim().length > 0
  ) {
    return true;
  }
  return false;
}

async function loadCurrentReport(threadId) {
  const currentReport = await getCurrentCoachingReport(threadId);
  if (isAnalysisReady(currentReport)) return currentReport;

  // Legacy fallback for older report rows that predate report:coaching facts.
  // Do not filter by a branded workflow key; the user-facing experience is a
  // generic coaching report now.
  const legacyResponse = await getLatestCommittedAnalysisRun(threadId);
  const legacyRun = extractLatestRun(legacyResponse);
  const legacyAnalysis = unwrapAnalysisJson(legacyRun);
  return isAnalysisReady(legacyAnalysis) ? legacyAnalysis : null;
}

function ConversationRow({ thread, onOpen }) {
  const treatment = thread.treatment;
  const status = thread.statusDetails;
  const canOpen = status.ready;

  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      onClick={() => {
        if (canOpen) onOpen(thread);
      }}
      aria-disabled={!canOpen}
      cursor={canOpen ? "pointer" : "default"}
      data-testid={`ui-spike-thread-row-${thread.id}`}
      _hover={canOpen ? { bg: "#f8fafc" } : undefined}
      _focusVisible={{ boxShadow: "inset 0 0 0 2px #2563eb" }}
    >
      <HStack
        spacing={{ base: 3, md: 4 }}
        px={{ base: 4, md: 5 }}
        py="18px"
        minH="88px"
        borderBottom="1px solid"
        borderColor="#eef2f7"
      >
        <Flex
          align="center"
          justify="center"
          flex="0 0 auto"
          w="52px"
          h="52px"
          borderRadius="16px"
          bg={treatment.bg}
          color={treatment.color}
        >
          <Icon as={treatment.icon} boxSize="25px" />
        </Flex>

        <Box flex="1" minW={0}>
          <Text
            color="#172033"
            fontSize={{ base: "16px", md: "17px" }}
            fontWeight="800"
            lineHeight="1.25"
            letterSpacing="0"
            noOfLines={1}
          >
            {thread.displayTitle}
          </Text>
          <Text
            color="#667085"
            fontSize="15px"
            fontWeight="500"
            lineHeight="1.35"
            mt="3px"
            noOfLines={1}
          >
            {pluralizeRecordings(thread.recording_count)} -{" "}
            {formatActivityDate(thread.activityDate)}
          </Text>
        </Box>

        <Badge
          colorScheme={status.colorScheme}
          variant="subtle"
          borderRadius="full"
          px={3}
          py={1}
          fontSize="12px"
          fontWeight="800"
          letterSpacing="0.02em"
          flex="0 0 auto"
        >
          {status.label}
        </Badge>

        <Icon
          as={MdChevronRight}
          boxSize={5}
          color={canOpen ? "#475467" : "#cbd5e1"}
          flex="0 0 auto"
        />
      </HStack>
    </Box>
  );
}

export default function UiSpikeConversationsPage() {
  const { userName, handleLogout, keycloak } = useAuth();
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [reportState, setReportState] = useState({
    loading: false,
    error: null,
    analysis: null,
    empty: false,
  });

  const loadThreads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listThreads({ limit: 50 });
      const items = response?.items || (Array.isArray(response) ? response : []);
      setThreads(items);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load threads.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const enrichedThreads = useMemo(() => {
    return threads
      .map((thread) => ({
        ...thread,
        activityDate: getActivityDate(thread),
        displayTitle: getDisplayTitle(thread),
        statusDetails: getThreadStatus(thread),
        treatment: getTreatment(thread),
      }))
      .sort((a, b) => {
        const aTime = a.activityDate ? a.activityDate.getTime() : 0;
        const bTime = b.activityDate ? b.activityDate.getTime() : 0;
        return bTime - aTime;
      });
  }, [threads]);

  const groupedThreads = useMemo(
    () => groupThreadsByWeek(enrichedThreads),
    [enrichedThreads],
  );

  useEffect(() => {
    if (!selectedThread) return undefined;
    let cancelled = false;
    setReportState({
      loading: true,
      error: null,
      analysis: null,
      empty: false,
    });

    loadCurrentReport(selectedThread.id)
      .then((analysis) => {
        if (cancelled) return;
        if (!analysis) {
          setReportState({
            loading: false,
            error: null,
            analysis: null,
            empty: true,
          });
          return;
        }
        setReportState({
          loading: false,
          error: null,
          analysis,
          empty: false,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setReportState({
          loading: false,
          error:
            e?.response?.data?.detail ||
            e?.message ||
            "Failed to load the report.",
          analysis: null,
          empty: false,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [selectedThread]);

  const closeReport = () => {
    setSelectedThread(null);
    setReportState({
      loading: false,
      error: null,
      analysis: null,
      empty: false,
    });
  };

  const identity = useMemo(
    () =>
      resolveIdentity({
        authUserName: userName,
        userInfo,
        tokenParsed: keycloak?.tokenParsed,
      }),
    [keycloak?.tokenParsed, userInfo, userName],
  );

  return (
    <Flex
      minH="100vh"
      bg="#f6f8fb"
      fontFamily={SPIKE_FONT_FAMILY}
      sx={{
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
      }}
      data-testid="ui-spike-conversations"
    >
      <Flex
        as="aside"
        position="fixed"
        left={0}
        top={0}
        bottom={0}
        w={{ base: "72px", md: "88px" }}
        direction="column"
        bg="white"
        borderRight="1px solid"
        borderColor="#e5e7eb"
        align="center"
        justify="space-between"
        py={6}
        zIndex={10}
      >
        <Box
          as="button"
          type="button"
          aria-label="Go to home"
          borderRadius="md"
          p={1}
          onClick={() => navigate("/")}
          _hover={{ bg: "#f1f5f9" }}
          _focusVisible={{ boxShadow: "0 0 0 2px #2563eb" }}
        >
          <Image
            src="/Logo.png"
            alt="Ethosphere"
            h="34px"
            maxW="46px"
            objectFit="contain"
            fallbackSrc="https://via.placeholder.com/34x34/2563eb/ffffff?text=E"
          />
        </Box>
        <Menu placement="right-end">
          <MenuButton
            as={Box}
            borderRadius="full"
            cursor="pointer"
            _hover={{ opacity: 0.88 }}
            _focusVisible={{ boxShadow: "0 0 0 2px #2563eb" }}
            aria-label="Open account menu"
          >
            <Avatar
              name={identity.accountName}
              size="sm"
              bg="#172033"
              color="white"
              fontSize="xs"
            />
          </MenuButton>
          <MenuList
            border="1px solid"
            borderColor="#e5e7eb"
            shadow="lg"
            borderRadius="lg"
            py={1}
            minW="190px"
            fontFamily={SPIKE_FONT_FAMILY}
          >
            <Box px={4} py={2}>
              <Text fontSize="xs" color="#94a3b8" fontWeight="700">
                Signed in as
              </Text>
              <Text
                fontSize="sm"
                fontWeight="700"
                color="#172033"
                maxW="160px"
                noOfLines={1}
              >
                {identity.accountName}
              </Text>
            </Box>
            <MenuDivider my={1} />
            <MenuItem
              fontSize="sm"
              fontWeight="700"
              color="red.500"
              onClick={handleLogout}
            >
              Log Out
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      <Box
        flex="1"
        ml={{ base: "72px", md: "88px" }}
        px={{ base: 5, md: 10, xl: 16 }}
        py={{ base: 8, md: 12 }}
      >
        <Box maxW="980px" mx="auto">
          <Flex
            align={{ base: "flex-start", md: "flex-end" }}
            justify="space-between"
            gap={4}
            mb={{ base: 8, md: 10 }}
            direction={{ base: "column", md: "row" }}
          >
            <Box minW={0}>
              <Text
                color="#667085"
                fontSize="13px"
                fontWeight="800"
                letterSpacing="0.08em"
                textTransform="uppercase"
                lineHeight="1.2"
                mb={3}
              >
                Threads
              </Text>
              <Heading
                color="#101828"
                fontWeight="800"
                lineHeight="0.96"
                letterSpacing="0"
                fontSize={{ base: "44px", md: "64px" }}
              >
                {greetingForDate()}, {identity.greetingName}
              </Heading>
            </Box>

            <Button
              leftIcon={<Icon as={MdRefresh} />}
              size="sm"
              variant="outline"
              borderRadius="md"
              onClick={loadThreads}
              isLoading={loading}
            >
              Refresh
            </Button>
          </Flex>

          {loading && (
            <Center py={20}>
              <Spinner size="lg" />
            </Center>
          )}

          {!loading && error && (
            <Alert status="error" borderRadius="md" data-testid="ui-spike-error">
              <AlertIcon />
              <Box flex={1}>
                <AlertDescription fontSize="sm">{error}</AlertDescription>
              </Box>
              <Button size="xs" onClick={loadThreads}>
                Retry
              </Button>
            </Alert>
          )}

          {!loading && !error && groupedThreads.length === 0 && (
            <Box
              bg="white"
              border="1px dashed"
              borderColor="#cbd5e1"
              borderRadius="12px"
              py={16}
              textAlign="center"
              data-testid="ui-spike-empty"
            >
              <Text color="#667085" fontSize="sm">
                No threads yet.
              </Text>
            </Box>
          )}

          {!loading && !error && groupedThreads.length > 0 && (
            <VStack align="stretch" spacing={8}>
              {groupedThreads.map((group) => (
                <Box key={group.key}>
                  <Text
                    color="#667085"
                    fontSize="13px"
                    fontWeight="800"
                    letterSpacing="0.10em"
                    textTransform="uppercase"
                    lineHeight="1.2"
                    mb={3}
                  >
                    {group.label}
                  </Text>
                  <Box
                    bg="white"
                    border="1px solid"
                    borderColor="#e5e7eb"
                    borderRadius="14px"
                    overflow="hidden"
                    boxShadow="0 1px 2px rgba(16,24,40,0.04)"
                  >
                    {group.items.map((thread) => (
                      <ConversationRow
                        key={thread.id}
                        thread={thread}
                        onOpen={setSelectedThread}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </Box>

      <Modal
        isOpen={Boolean(selectedThread)}
        onClose={closeReport}
        isCentered
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          maxH="92vh"
          borderRadius="18px"
          overflow="hidden"
          bg="#f8fafc"
          data-testid="ui-spike-report-modal"
        >
          <ModalHeader
            bg="white"
            borderBottom="1px solid"
            borderColor="#e5e7eb"
            py={4}
            pr={12}
          >
            <HStack spacing={3} minW={0}>
              <Text fontSize="md" fontWeight="800" noOfLines={1}>
                {selectedThread?.displayTitle || "Conversation report"}
              </Text>
              <Badge colorScheme="green" borderRadius="full" px={3}>
                Ready
              </Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={3} />
          <ModalBody p={0} bg="#f8fafc">
            {reportState.loading && (
              <Center minH="360px">
                <VStack spacing={3}>
                  <Spinner size="lg" />
                  <Text color="#667085" fontSize="sm">
                    Loading report
                  </Text>
                </VStack>
              </Center>
            )}

            {!reportState.loading && reportState.error && (
              <Box p={6}>
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    {reportState.error}
                  </AlertDescription>
                </Alert>
              </Box>
            )}

            {!reportState.loading && !reportState.error && reportState.empty && (
              <Center minH="360px" px={6} textAlign="center">
                <VStack spacing={2}>
                  <Heading size="sm">Report not ready yet</Heading>
                  <Text color="#667085" fontSize="sm" maxW="420px">
                    This conversation is marked ready in the list, but no
                    coaching report was returned for the preview.
                  </Text>
                </VStack>
              </Center>
            )}

            {!reportState.loading && !reportState.error && reportState.analysis && (
              <CoachingReportView
                analysis={reportState.analysis}
                threadId={selectedThread?.id}
                displayMode="modal"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
